/**
 * The Maldives (players: "I want to see turtles and dolphins"): a getaway you can take from anywhere. The flight out
 * and the seaplane to a resort island, a ring of white sand on a turquoise lagoon; the house reef to snorkel (free, the
 * resort's own: green turtles grazing the sea grass, baby blacktip sharks in the shallows, a stingray on the sand); a
 * dolphin cruise at sundown on a dhoni (the pod comes to ride the bow); a night in a villa on stilts over the water.
 * What you've seen goes in a log. Prices in dollars, at the going rate. Flying home brings you back where you were.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { USD_NGN } from './travel.ts';

const usd = (n: number) => Math.round((n * USD_NGN) / 1000) * 1000;
/** the flights there and back and the seaplane to the island */
export const MALDIVES_TRIP_USD = 2_400, CRUISE_USD = 95, VILLA_USD = 1_150;
export const maldivesTripPrice = () => usd(MALDIVES_TRIP_USD);
export const cruisePrice = () => usd(CRUISE_USD);
export const villaPrice = () => usd(VILLA_USD);
/** hours in the air, each way (with the change at Malé and the seaplane) */
export const MALDIVES_FLIGHT_MIN = 9 * 60;

/** what there is to see (ids the island places), and what the log calls it */
export const MALDIVES_LIFE: Record<string, string> = {
  turtle: 'a green sea turtle', dolphins: 'a pod of spinner dolphins', blacktip: 'baby blacktip sharks in the shallows', stingray: 'a stingray on the sand', jump: 'a dolphin leaping clear of the water',
};

export interface MaldivesState { trips: number; seen: string[]; cruises: number; nights: number; there: boolean }
export const maldivesOf = (s: LifeState): MaldivesState => ((s as LifeState & { maldives?: MaldivesState }).maldives ??= { trips: 0, seen: [], cruises: 0, nights: 0, there: false });

/** the trip: paid now, the flight's hours pass */
export function flyToMaldives(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, maldivesTripPrice(), 'The Maldives: flights and the seaplane')) return { ok: false, line: `The trip is $${MALDIVES_TRIP_USD.toLocaleString()} (the flights, the seaplane out to the island): you can't afford it yet.`, events: [] };
  const m = maldivesOf(s); m.trips++; m.there = true;
  if (m.trips === 1) s.history.push({ t: s.minutes, text: 'A first trip to the Maldives.' });
  return { ok: true, line: 'Nine hours, a change at Malé, then a seaplane low over the atolls: rings of white sand in water every blue there is.', events: advance(s, MALDIVES_FLIGHT_MIN, { busy: true }) };
}
export function flyHomeFromMaldives(s: LifeState): { line: string; events: LifeEvent[] } {
  const m = maldivesOf(s); m.there = false;
  s.needs.fun = Math.min(100, s.needs.fun + 10);
  return { line: 'The seaplane, Malé, the long flight home. Sand still in your shoes.', events: advance(s, MALDIVES_FLIGHT_MIN, { busy: true }) };
}

/** a sighting: a new one is a thrill (the line to show), a repeat is just nice */
export function sawInMaldives(s: LifeState, id: string): string | null {
  const m = maldivesOf(s);
  s.needs.fun = Math.min(100, s.needs.fun + 2);
  if (m.seen.includes(id) || !MALDIVES_LIFE[id]) return null;
  m.seen.push(id);
  if (id === 'turtle') s.history.push({ t: s.minutes, text: 'Swam with a green sea turtle in the Maldives.' });
  if (id === 'jump') s.history.push({ t: s.minutes, text: 'Saw a dolphin leap clear of the water in the Maldives.' });
  s.needs.fun = Math.min(100, s.needs.fun + 8);
  return `New in your log: ${MALDIVES_LIFE[id]} (${m.seen.length} of ${Object.keys(MALDIVES_LIFE).length}).`;
}

/** out of the water after a swim (the clock's gone on by itself meanwhile) */
export function snorkelDone(s: LifeState): string {
  s.needs.fun = Math.min(100, s.needs.fun + 12); s.needs.energy = Math.max(0, s.needs.energy - 5);
  const m = maldivesOf(s);
  return m.seen.length ? `Out of the water, salt on your lips. Your log: ${m.seen.length} of ${Object.keys(MALDIVES_LIFE).length} seen.` : 'Out of the water, salt on your lips.';
}
/** a coconut at the beach bar (the resort's: on the house) */
export function coconut(s: LifeState): string {
  s.needs.hunger = Math.min(100, s.needs.hunger + 6); s.needs.fun = Math.min(100, s.needs.fun + 4); s.needs.energy = Math.min(100, s.needs.energy + 3);
  return 'The barman takes a machete to a green coconut, three clean strokes, and slides it over with a straw. Cold, sweet, a little fizzy.';
}

export function buyCruise(s: LifeState): { ok: boolean; line: string } {
  if (!spend(s, cruisePrice(), 'A dolphin cruise, the Maldives')) return { ok: false, line: `The dolphin cruise is $${CRUISE_USD}: you can't afford it.` };
  maldivesOf(s).cruises++;
  return { ok: true, line: 'Aboard the dhoni: a wooden boat with a high curved prow, cushions on the deck, the crew casting off.' };
}
export function cruiseDone(s: LifeState): LifeEvent[] {
  s.needs.fun = Math.min(100, s.needs.fun + 25);
  return advance(s, 90, { busy: true });
}

/** a night over the water: slept well (energy full), the morning */
export function villaNight(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, villaPrice(), 'An overwater villa, the Maldives')) return { ok: false, line: `A night in an overwater villa is $${VILLA_USD.toLocaleString()}: you can't afford it.`, events: [] };
  const m = maldivesOf(s); m.nights++;
  const day = Math.floor(s.minutes / 1440), wake = (s.minutes % 1440 < 5 * 60 ? day : day + 1) * 1440 + 7 * 60;
  const events = advance(s, wake - s.minutes, { asleep: true });
  s.needs.energy = 100; s.needs.fun = Math.min(100, s.needs.fun + 12);
  if (m.nights === 1) s.history.push({ t: s.minutes, text: 'A night in a villa over the water, in the Maldives.' });
  return { ok: true, line: 'You sleep to the sound of the water under the floor. In the morning: the lagoon through the glass panel by the bed, a turtle going by.', events };
}
